
import React, { useState } from 'react';
import { Customer, Ticket, Employee, TicketStatus, RepairStageStatus, AppSettings, CommunicationType, RepairChecklist, AppointmentModalData, InventoryItem } from '../types'; 
import { Button } from './Button';
import { TicketItem } from './TicketItem'; 

interface CustomerProfilePageProps {
  customer?: Customer;
  customerTickets: Ticket[];
  employees: Employee[]; 
  appSettings: AppSettings; 
  currentUser: Employee | null;
  inventoryItems: InventoryItem[];
  onAddCustomerNote: (customerId: string, noteText: string) => void;
  onOpenUpdateStatusModal: (ticketId: string, currentStatus: TicketStatus) => void;
  onViewCustomerProfile: (customerName: string) => void; 
  onOpenPaymentModal: (ticketId: string, amountDue: number, customerName: string) => void;
  onOpenPrintModal: (title: string, content: React.ReactNode, contentClassName?: string) => void; 
  onShowCustomerPortal?: (ticketId: string) => void;
  onAddInternalTicketNote: (ticketId: string, noteText: string, employeeName?: string) => void;
  onUpdateRepairStage: (ticketId: string, stageId: string, newStatus: RepairStageStatus, notes?: string) => void; 
  onAddCommunicationLog: (ticketId: string, type: CommunicationType, content: string, recipient?: string, staffName?: string) => void;
  onOpenAppointmentModal: (data?: AppointmentModalData) => void;
  onAddPartToTicket: (ticketId: string, inventoryItemId: string, quantityUsed: number, addPriceToTicketTotal: boolean) => void;
  repairChecklists: RepairChecklist[];
  onUpdateTicketChecklistProgress: (ticketId: string, checklistItemId: string, completed: boolean) => void;
}

// Icons
const UserIconSolid: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6 mr-2 text-[var(--color-primary)]" {...props}>
    <path fillRule="evenodd" d="M7.5 6a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM3.751 20.105a8.25 8.25 0 0116.498 0 .75.75 0 01-.437.695A18.683 18.683 0 0112 22.5c-2.786 0-5.433-.608-7.812-1.7a.75.75 0 01-.437-.695z" clipRule="evenodd" />
  </svg>
);
const PhoneIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 mr-2 text-slate-500" {...props}><path d="M2 3.5A1.5 1.5 0 013.5 2h1.148a1.5 1.5 0 011.465 1.175l.716 3.066a1.5 1.5 0 01-1.652 1.776l-1.052-.503a13.25 13.25 0 006.05 6.05l.503-1.052a1.5 1.5 0 011.776-1.652l3.066.716A1.5 1.5 0 0118 15.352V16.5a1.5 1.5 0 01-1.5 1.5H15c-5.523 0-10-4.477-10-10V5a1.5 1.5 0 011.5-1.5H5.352a1.5 1.5 0 011.465 1.175l.716 3.066A1.5 1.5 0 015.219 8.45l-1.052-.503zM2 3.5A1.5 1.5 0 013.5 2H5c.098 0 .193.013.284.038L4.19 4.094a1.515 1.515 0 01-.62 1.43L2.04 7.004A1.5 1.5 0 012 5.5v-2zM16.5 18H15a.5.5 0 01-.5-.5V16.85c0-.098-.013-.193-.038-.284l2.054-1.096a1.515 1.515 0 01-1.43.62L13.5 17.96A1.5 1.5 0 0114.5 18h2z"/></svg>
);
const EmailIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
 <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 mr-2 text-slate-500" {...props}><path d="M3 4a2 2 0 00-2 2v1.161l8.441 4.221a1.25 1.25 0 001.118 0L19 7.162V6a2 2 0 00-2-2H3z" /><path d="M19 8.839l-7.77 3.885a2.75 2.75 0 01-2.46 0L1 8.839V14a2 2 0 002 2h14a2 2 0 002-2V8.839z" /></svg>
);
const CalendarDaysIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
 <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 mr-2 text-slate-500" {...props}><path fillRule="evenodd" d="M5.75 2a.75.75 0 01.75.75V4h7V2.75a.75.75 0 011.5 0V4h.25A2.75 2.75 0 0118 6.75v8.5A2.75 2.75 0 0115.25 18H4.75A2.75 2.75 0 012 15.25v-8.5A2.75 2.75 0 014.75 4H5V2.75A.75.75 0 015.75 2zm-1 5.5c-.69 0-1.25.56-1.25 1.25v6.5c0 .69.56 1.25 1.25 1.25h10.5c.69 0 1.25-.56 1.25-1.25v-6.5c0-.69-.56-1.25-1.25-1.25H4.75z" clipRule="evenodd" /></svg>
);
const ClipboardIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
 <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-6 h-6 mr-2 text-[var(--color-primary)]" {...props}><path fillRule="evenodd" d="M2 3.5A1.5 1.5 0 013.5 2H6a1.5 1.5 0 011.5 1.5v.5h5V3.5A1.5 1.5 0 0114 2h2.5A1.5 1.5 0 0118 3.5v13a1.5 1.5 0 01-1.5 1.5H3.5A1.5 1.5 0 012 16.5v-13zM7.5 4H4.5v12h11V4h-3v1A1.5 1.5 0 0111 6.5H9A1.5 1.5 0 017.5 5V4z" clipRule="evenodd" /></svg>
);
const PencilIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
 <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-6 h-6 mr-2 text-[var(--color-primary)]" {...props}><path d="M2.695 14.763l-1.262 3.154a.5.5 0 00.65.65l3.155-1.262a4 4 0 001.343-.885L17.5 5.5a2.121 2.121 0 00-3-3L3.58 13.42a4 4 0 00-.885 1.343z" /></svg>
);


export const CustomerProfilePage: React.FC<CustomerProfilePageProps> = ({
  customer,
  customerTickets,
  appSettings,
  currentUser,
  inventoryItems,
  onAddCustomerNote,
  onOpenUpdateStatusModal,
  onViewCustomerProfile,
  onOpenPaymentModal,
  onOpenPrintModal, 
  onShowCustomerPortal,
  onAddInternalTicketNote,
  onUpdateRepairStage,
  onAddCommunicationLog,
  onOpenAppointmentModal,
  onAddPartToTicket,
  repairChecklists,
  onUpdateTicketChecklistProgress,
}) => {
  const [newNote, setNewNote] = useState('');
  const isCurrentUserInactive = currentUser?.status === 'Inactive';

  if (!customer) {
    return (
      <div className="bg-white p-8 rounded-lg shadow text-center text-slate-500">
        <p>Customer not found or no customer selected.</p>
        <p className="text-sm">Please search for a customer to view their profile.</p>
      </div>
    );
  }

  const handleAddNoteSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isCurrentUserInactive) {
        alert("Inactive users cannot add customer notes.");
        return;
    }
    if (newNote.trim()) {
      onAddCustomerNote(customer.id, newNote.trim());
      setNewNote('');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', { 
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' 
    });
  };

  return (
    <div className="space-y-8">
      <div className="bg-white p-6 rounded-lg shadow-xl">
        <div className="flex items-center mb-4">
          <UserIconSolid />
          <h1 className="text-2xl font-bold text-[var(--color-primary)]">{customer.name}</h1>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          {customer.phoneNumber && (
            <div className="flex items-center">
              <PhoneIcon />
              <div>
                <p className="text-slate-500">Phone Number</p>
                <p className="text-slate-700 font-medium">{customer.phoneNumber}</p>
              </div>
            </div>
          )}
          {customer.email && (
            <div className="flex items-center">
              <EmailIcon />
               <div>
                <p className="text-slate-500">Email Address</p>
                <p className="text-slate-700 font-medium truncate" title={customer.email}>{customer.email}</p>
              </div>
            </div>
          )}
          <div className="flex items-center">
            <CalendarDaysIcon />
             <div>
              <p className="text-slate-500">Customer Since</p>
              <p className="text-slate-700 font-medium">{formatDate(customer.createdAt)}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-xl">
        <div className="flex items-center mb-4">
            <ClipboardIcon />
            <h2 className="text-xl font-semibold text-slate-700">Repair History ({customerTickets.length})</h2>
        </div>
        {customerTickets.length > 0 ? (
          <div className="space-y-4">
            {customerTickets.map(ticket => (
              <TicketItem 
                key={ticket.id} 
                ticket={ticket} 
                appSettings={appSettings} 
                currentUser={currentUser}
                inventoryItems={inventoryItems}
                onOpenUpdateStatusModal={onOpenUpdateStatusModal}
                onViewCustomerProfile={onViewCustomerProfile} 
                onOpenPaymentModal={onOpenPaymentModal}
                onOpenPrintModal={onOpenPrintModal}
                onShowCustomerPortal={onShowCustomerPortal}
                onAddInternalTicketNote={onAddInternalTicketNote}
                onUpdateRepairStage={onUpdateRepairStage}
                onAddCommunicationLog={onAddCommunicationLog}
                onOpenAppointmentModal={onOpenAppointmentModal}
                onAddPartToTicket={onAddPartToTicket}
                repairChecklists={repairChecklists}
                onUpdateTicketChecklistProgress={onUpdateTicketChecklistProgress}
              />
            ))}
          </div>
        ) : (
          <p className="text-slate-500">No repair history found for this customer.</p>
        )}
      </div>

      <div className="bg-white p-6 rounded-lg shadow-xl">
         <div className="flex items-center mb-4">
            <PencilIcon />
            <h2 className="text-xl font-semibold text-slate-700">Customer Notes</h2>
        </div>
        <form onSubmit={handleAddNoteSubmit} className="mb-6">
          <textarea
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            placeholder={isCurrentUserInactive ? "Inactive users cannot add notes" : "Add a new note..."}
            rows={3}
            className="w-full p-3 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] focus:border-[var(--color-primary)] sm:text-sm disabled:bg-slate-50 disabled:cursor-not-allowed"
            aria-label="New customer note"
            disabled={isCurrentUserInactive}
          ></textarea>
          <Button 
            type="submit" 
            variant="primary" 
            className="mt-2 text-sm"
            disabled={isCurrentUserInactive || !newNote.trim()}
            title={isCurrentUserInactive ? "Inactive users cannot add notes" : undefined}
          >
            Add Note
          </Button>
        </form>
        {customer.notes.length > 0 ? (
          <div className="space-y-3 max-h-60 overflow-y-auto pr-2">
            {[...customer.notes].reverse().map((note, index) => ( 
              <div key={index} className="bg-slate-50 p-3 rounded-md border border-slate-200">
                <p className="text-sm text-slate-700">{note.text}</p>
                <p className="text-xs text-slate-500 mt-1">{formatDate(note.date)}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-slate-500">No notes for this customer yet.</p>
        )}
      </div>
    </div>
  );
};
